/**
 * Class for handling subsites REST request
 * @copyright 2021-2022 Soter Technologies, LLC. All rights reserved.
 * @file subsites.js
 * @author Matt Schreider, Kyle Watkins, Paul Scala
*/
class Subsites {
    /**
     * Initialize class
     * @param {object} axios Axios instance
     * @param {object} session User session handler
     */
    constructor(axios, session) {
        this.axios = axios
        this.session = session
    }

    /**
     * @description Gets subsites
     * @param {string} siteId Filter by siteId
     * @param {object} searchFilters
     * @param {number} searchFilters.page Filter by page number
     * @param {string} searchFilters.order Order by ASC (ascending) or DESC (descending)
     * @param {string} searchFilters.search Search by value
     * @param {string} searchFilters.sortBy Sort by field type
     * @param {boolean} searchFilters.isActive Filter by is active
     * @param {boolean} searchFilters.forceRoleTwoFactorAuth Filter by if two factor auth is forced
     * @param {boolean} searchFilters.hasTenantId Filter by if site has tenant id
     * @returns {ResponseObject} Api response object
     */
    async getSubSites(siteId = null, {page = 0, order = "ASC", search = "", sortBy = "", isActive = null, forceRoleTwoFactorAuth = null, hasTenantId = null} = {}) {
        let searchFilters = {
            order,
            ...(search === "") ? {} : {search: encodeURIComponent(search)},
            ...(sortBy === "") ? {} : {sortBy},
            ...(isActive !== null && {isActive: isActive}),
            ...(forceRoleTwoFactorAuth !== null && {forceRoleTwoFactorAuth: forceRoleTwoFactorAuth}),
            ...(hasTenantId !== null && {hasTenantId: hasTenantId})
        }
        
        let query = this.session.getQuery({page, siteId, searchFilters})

        let config = {
            method: 'get',
            url: `/api/sites${"?" + query}`,
        }

        let res = await this.axios(config)

        return (
            {
                status: res.status,
                statusText: res.statusText,
                isSuccess: (res.status >= 200 && res.status < 300),
                data: res.data
            }
        )
    }

    /**
     * @description Gets subsites
     * @param {string} siteId Filter by siteId
     * @param {object} searchFilters
     * @param {number} searchFilters.page Filter by page number
     * @param {string} searchFilters.order Order by ASC (ascending) or DESC (descending)
     * @param {string} searchFilters.search Search by value
     * @param {string} searchFilters.sortBy Sort by field type
     * @param {boolean} searchfilters.isActive Filter by is active
     * @param {string} searchFilters.countOnly Get count only, default true
     * @param {string} searchFilters.country Filter by country, default null
     * @param {string} searchfilters.siteId Site id to filter subsites by
     * @param {boolean} searchFilters.isRootSite Filter by root site
     * @param {boolean} searchFilters.forceRoleTwoFactorAuth Filter by if two factor auth is forced
     * @param {string[]} searchFilters.hasAdditionalUiFeatures Filter by if subsite has Additional UI features, defaults to null
     * @param {boolean} searchFilters.hasTenantId Filter by if site has tenant id
     * @param {string} searchFilters.startTime Filter by start time
     * @param {string} searchFilters.endTime Filter by end time
     * @returns {ResponseObject} Api response object
     */
    async getAllSubSites({page = 0, order = "ASC", search = "", sortBy = "", isActive = null, siteId = null, country = "", countOnly = false, isRootSite = null, forceRoleTwoFactorAuth = null, hasAdditionalUiFeatures = null, hasTenantId = null, startTime = '', endTime = ''} = {}) {
        let searchFilters = {
            order,
            ...(search === "") ? {} : {search},
            ...(sortBy === "") ? {} : {sortBy},
            ...(isActive !== null && {isActive: isActive}),
            ...(siteId !== null && {siteId: siteId}),
            countOnly,
            ...(isRootSite !== null && {isRootSite: isRootSite}),
            ...(forceRoleTwoFactorAuth !== null && {forceRoleTwoFactorAuth: forceRoleTwoFactorAuth}),
            ...(hasAdditionalUiFeatures !== null && {hasAdditionalUiFeatures: hasAdditionalUiFeatures}),
            ...(hasTenantId !== null && {hasTenantId: hasTenantId}),
            ...(country !== "" && {country: country}),
            ...((startTime === "" || startTime === undefined) ? {} : {startTime: new Date(startTime).getTime()}),
            ...((endTime === "" || endTime === undefined) ? {} : {endTime: new Date(endTime).getTime()})
        }
        
        let query = this.session.getQuery({page, searchFilters})

        let config = {
            method: 'get',
            url: `/api/sites/all${"?" + query}`,
        }

        let res = await this.axios(config)

        return (
            {
                status: res.status,
                statusText: res.statusText,
                isSuccess: (res.status >= 200 && res.status < 300),
                data: res.data
            }
        )
    }

    /**
     * @description Get subsite count
     * @param {string} siteId Filter by siteId
     * @param {object} searchFilters
     * @param {number} searchFilters.page Filter by page number
     * @param {string} searchFilters.order Order by ASC (ascending) or DESC (descending)
     * @param {string} searchFilters.search Search by value
     * @param {string} searchFilters.sortBy Sort by field type
     * @param {boolean} searchfilters.isActive Filter by is active
     * @param {string} searchFilters.countOnly Get count only, default true
     * @returns {ResponseObject} Api response object
     */
    async getCount(siteId = null, {page = 0, order = "ASC", search = "", sortBy = "", countOnly = true} = {}) {
        let searchFilters = {
            order,
            ...(search === "") ? {} : {search},
            ...(sortBy === "") ? {} : {sortBy},
            countOnly
        }

        let query = this.session.getQuery({page, siteId, searchFilters})
        let config = {
            method: 'get',
            url: `/api/sites${"?" + query}`,
        }

        let res = await this.axios(config)

        return (
            {
                status: res.status,
                statusText: res.statusText,
                isSuccess: (res.status >= 200 && res.status < 300),
                data: res.data
            }
        )
    }

    /**
     * @description Get a single subsite's information by id
     * @param {object} params Call parameters
     * @param {string} params.id Subsite uuid  
     * @returns {ResponseObject} Api response object
     */
    async getSubsite({id}) {
        let config = {
            method: 'get',
            url: `/api/sites/${id}?populate=simple`,
        }
          
        let res = await this.axios(config)
        
        return ({
            status: res.status,
            statusText: res.statusText,
            isSuccess: (res.status >= 200 && res.status < 300),
            data: res.data
        })
    }

    /**
     * @description Deletes a specified subsite by id
     * @param {Object} params Function parameters
     * @param {string} params.id Subsite id
     * @returns {ResponseObject} Api response object
     */
    async deleteSubsite({id}) {
        let config = {
            method: 'delete',
            url: `/api/sites/${id}`,
            headers: { 
                'Content-Type': 'application/json'
            },
        }
          
        let res = await this.axios(config)
        
        return ({
            status: res.status,
            statusText: res.statusText,
            isSuccess: (res.status >= 200 && res.status < 300),
            data: res.data
        })
    }

    /**
     * @description Creates a subsite
     * @param {object} params Function parameters
     * @param {string} params.siteId Site id of subsite
     * @param {string} params.name Subsite name
     * @param {string} params.address Subsite street address
     * @param {string} params.city Subsite city
     * @param {string} params.state Subsite state
     * @param {string} params.country Subsite country
     * @param {string} params.zip Subsite zipcode
     * @param {string} params.timezone Subsite timezone
     * @param {boolean} params.forceRoleTwoFactorAuth Force role two factor auth
     * @param {string[]} params.additionalUiFeatures Subsite Additional UI features, defaults to null
     * @returns {ResponseObject} Api response object
     */
    async createSubsite({siteId = null, name = null, address = null, city = null, state = null, country = null, zip = null, timezone = null, forceRoleTwoFactorAuth = null, additionalUiFeatures = []}) {
        if (siteId === null) {
            siteId = this.session.getSiteId()
        }

        let data = {
            "Name": name,
            "ParentSite": siteId,
            "Address": address,
            "State": state,
            "Zip": zip,
            "City": city,
            "Country": country,
            "TimeZone": timezone,
            "IsActive": true,
            "SubscriptionType": "Monthly",
            "OrganizationType": "Office",
            "ForceRoleTwoFactorAuth": forceRoleTwoFactorAuth,
            "AdditionalUiFeatures": additionalUiFeatures
        }

        let config = {
            method: 'post',
            url: '/api/sites',
            headers: { 
                'Content-Type': 'application/json'
            },
            data: data
        }
          
        let res = await this.axios(config)
        
        return ({
            status: res.status,
            statusText: res.statusText,
            isSuccess: (res.status >= 200 && res.status < 300),
            data: res.data
        })
    }

    /**
     * @description Save subsite settings by id
     * @param {Object} params Function parameters
     * @param {string} params.id Subsite id
     * @param {string} params.name Subsite name
     * @param {string} params.address Subsite street address
     * @param {string} params.city Subsite city
     * @param {string} params.state Subsite state
     * @param {string} params.country Subsite country
     * @param {string} params.zip Subsite zipcode
     * @param {string} params.timezone Subsite timezone
     * @param {boolean} params.isActive Subsite is active
     * @param {string} params.tenantId Subsite tenantId
     * @param {boolean} searchFilters.forceRoleTwoFactorAuth Subsite forceRoleTwoFactorAuth
     * @param {{ guid: string }[]} params.additionalUiFeatures Subsite Additional UI features
     * @returns {ResponseObject} Api response object
     */
    async saveSubsite({id = null, name = null, address = null, city = null, state = null, country = null, zip = null, timezone = null, isActive = null, tenantId = null, forceRoleTwoFactorAuth = [], additionalUiFeatures = null}) {
        let data = {
            "Name": name,
            "Address": address,
            "State": state,
            "Zip": zip,
            "City": city,
            "Country": country,
            "TimeZone": timezone,
            "IsActive": (isActive === "true" || isActive === true),
            "TenantId": tenantId,
            ...(forceRoleTwoFactorAuth.length !== 0 && {"ForceRoleTwoFactorAuth": forceRoleTwoFactorAuth}),
            ...(additionalUiFeatures !== null && {"AdditionalUiFeatures": additionalUiFeatures.map(d => d.guid)})
        }

        let config = {
            method: 'put',
            url: `/api/sites/${id}`,
            headers: { 
                'Content-Type': 'application/json'
            },
            data: data
        }
          
        let res = await this.axios(config)
        
        return ({
            status: res.status,
            statusText: res.statusText,
            isSuccess: (res.status >= 200 && res.status < 300),
            data: res.data
        })
    }

    /**
     * @description Get metrics for a subsite
     * @param {string} siteId Site id to get lists for
     * @param {Object} params Function parameters
     * @param {number} params.startTime Start Time for getting alerts
     * @param {number} params.endTime End Time for getting alerts
     * @param {string} params.method Method of getting alert count, defaults to none
     * @returns {ResponseObject} Api response object
     */
    async getMetrics(siteId, {startTime, endTime, method = "None"}) {
        // Create query, conditionally add startTime and endTime
        let query = this.session.getQuery({
            searchFilters: {
                ...((startTime === "" || startTime === undefined) ? {} : {startTime: startTime}),
                ...((endTime === "" || endTime === undefined) ? {} : {endTime: endTime}),
                method: method
            }
        })
        
        let config = {
            method: 'get',
            url: `/api/sites/${siteId}/metrics${"?" + query}`,
        }

        let res = await this.axios(config)
        
        return (
            { 
                status: res.status,
                statusText: res.statusText,
                isSuccess: (res.status >= 200 && res.status < 300),
                data: res.data
            }
        )
    }
}

export default Subsites